import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Swords, Trophy, Timer, CheckCircle2, XCircle, ArrowRight,
  Loader2, Crown, Target, Zap, GraduationCap, Users, RotateCcw, Share2, Copy
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { shareChallenge } from '../utils/whatsapp';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChallengePage = () => {
  const { challengeId } = useParams();
  const [phase, setPhase] = useState('loading'); // loading | intro | quiz | results
  const [challenge, setChallenge] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [results, setResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const timerRef = useRef(null);

  useEffect(() => {
    const fetchChallenge = async () => {
      try {
        const res = await axios.get(`${API}/challenges/view/${challengeId}`);
        setChallenge(res.data);
        setPhase('intro');
        // Also fetch leaderboard
        try {
          const lb = await axios.get(`${API}/challenges/leaderboard/${challengeId}`);
          setLeaderboard(lb.data.leaderboard || []);
        } catch (e) { /* ignore */ }
      } catch (err) {
        setError(err.response?.data?.detail || 'Challenge not found');
        setPhase('error');
      }
    };
    if (challengeId) fetchChallenge();
  }, [challengeId]);

  const startQuiz = () => {
    if (!name.trim()) return toast.error('Enter your name first!');
    setPhase('quiz');
    setCurrentQ(0);
    setAnswers({});
    setTimeElapsed(0);
    timerRef.current = setInterval(() => setTimeElapsed(prev => prev + 1), 1000);
  };

  const selectAnswer = (option) => {
    setAnswers(prev => ({ ...prev, [String(currentQ)]: option }));
  };

  const nextQuestion = () => {
    if (currentQ < (challenge?.questions?.length || 0) - 1) {
      setCurrentQ(prev => prev + 1);
    }
  };

  const prevQuestion = () => {
    if (currentQ > 0) setCurrentQ(prev => prev - 1);
  };

  const submitChallenge = async () => {
    clearInterval(timerRef.current);
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/challenges/attempt`, {
        challenge_id: challengeId,
        answers,
        time_taken_seconds: timeElapsed,
        challenger_name: name.trim(),
      });
      setResults(res.data);
      setPhase('results');
      // Refresh leaderboard
      try {
        const lb = await axios.get(`${API}/challenges/leaderboard/${challengeId}`);
        setLeaderboard(lb.data.leaderboard || []);
      } catch (e) { /* ignore */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Challenge link copied!');
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  if (phase === 'loading') {
    return (
      <div className="min-h-screen bg-[#0A0A0F] flex items-center justify-center" data-testid="challenge-loading">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="min-h-screen bg-[#0A0A0F] flex items-center justify-center p-4" data-testid="challenge-error">
        <Card className="max-w-md w-full p-8 text-center rounded-2xl bg-white/5 backdrop-blur-xl border-white/10">
          <XCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
          <h2 className="text-xl font-bold text-white mb-2">Challenge Not Found</h2>
          <p className="text-white/60 mb-6">{error}</p>
          <Link to="/"><Button className="rounded-xl bg-gradient-to-r from-orange-500 to-red-500">Go to iStudyPro</Button></Link>
        </Card>
      </div>
    );
  }

  // ─── INTRO PHASE ──────────────────────────────────
  if (phase === 'intro') {
    return (
      <div className="min-h-screen bg-[#0A0A0F]" data-testid="challenge-intro">
        <div className="max-w-lg mx-auto px-4 py-10 space-y-6">
          {/* Challenge Header */}
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg shadow-orange-500/30">
              <Swords className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white">Quiz Challenge!</h1>
            <p className="text-white/60 mt-1">{challenge.subject_name || 'General Knowledge'}</p>
          </motion.div>

          {/* Creator Card */}
          <Card className="p-5 rounded-2xl bg-white/5 backdrop-blur-xl border-orange-500/20" data-testid="challenge-creator-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-white font-bold">
                {(challenge.creator_name || 'S')[0].toUpperCase()}
              </div>
              <div>
                <p className="text-sm font-semibold text-white">{challenge.creator_name}</p>
                <p className="text-xs text-white/50">Challenge Creator</p>
              </div>
              <div className="ml-auto text-right">
                <p className="text-2xl font-bold text-orange-400">{challenge.creator_score}%</p>
                <p className="text-xs text-white/50">Grade {challenge.creator_grade}</p>
              </div>
            </div>
            <p className="text-sm text-white/70 italic">"{challenge.message}"</p>
          </Card>

          {/* Challenge Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 rounded-xl bg-white/5 border border-white/10 text-center">
              <Target className="w-5 h-5 mx-auto text-cyan-400 mb-1" />
              <p className="text-lg font-bold text-white">{challenge.total_questions}</p>
              <p className="text-[10px] text-white/50">Questions</p>
            </div>
            <div className="p-3 rounded-xl bg-white/5 border border-white/10 text-center">
              <Users className="w-5 h-5 mx-auto text-purple-400 mb-1" />
              <p className="text-lg font-bold text-white">{challenge.attempt_count}</p>
              <p className="text-[10px] text-white/50">Attempts</p>
            </div>
            <div className="p-3 rounded-xl bg-white/5 border border-white/10 text-center">
              <Trophy className="w-5 h-5 mx-auto text-yellow-400 mb-1" />
              <p className="text-lg font-bold text-white">{challenge.best_challenger_score}%</p>
              <p className="text-[10px] text-white/50">Best Score</p>
            </div>
          </div>

          {/* Name Input + Start */}
          <Card className="p-5 rounded-2xl bg-white/5 border-white/10 space-y-4">
            <div>
              <label className="text-sm font-medium text-white/80 mb-2 block">Your Name</label>
              <Input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Enter your name..."
                className="bg-white/5 border-white/20 text-white"
                data-testid="challenge-name-input"
              />
            </div>
            <Button
              onClick={startQuiz}
              disabled={!name.trim()}
              className="w-full h-12 rounded-xl gap-2 bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold text-base"
              data-testid="challenge-start-btn"
            >
              <Swords className="w-5 h-5" /> Accept Challenge
            </Button>
          </Card>

          {/* Leaderboard Preview */}
          {leaderboard.length > 0 && (
            <Card className="p-4 rounded-2xl bg-white/5 border-white/10" data-testid="challenge-leaderboard">
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2"><Crown className="w-4 h-4 text-yellow-400" /> Leaderboard</h3>
              <div className="space-y-2">
                {leaderboard.slice(0, 5).map((e, i) => (
                  <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg ${e.is_creator ? 'bg-orange-500/10 border border-orange-500/20' : 'bg-white/[0.03]'}`}>
                    <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${i === 0 ? 'bg-yellow-500/20 text-yellow-400' : i === 1 ? 'bg-gray-400/20 text-gray-300' : 'bg-white/10 text-white/50'}`}>{i + 1}</span>
                    <span className="flex-1 text-sm text-white truncate">{e.name} {e.is_creator && <span className="text-[10px] text-orange-300">(creator)</span>}</span>
                    <span className="text-sm font-bold text-white">{e.score}%</span>
                    <span className="text-[10px] text-white/40">{formatTime(e.time_taken_seconds)}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* CTA */}
          <div className="text-center pt-2">
            <Link to="/" className="text-xs text-white/40 hover:text-white/60">Powered by iStudyPro</Link>
          </div>
        </div>
      </div>
    );
  }

  // ─── QUIZ PHASE ───────────────────────────────────
  if (phase === 'quiz') {
    const q = challenge.questions[currentQ];
    const total = challenge.questions.length;
    const answered = Object.keys(answers).length;

    return (
      <div className="min-h-screen bg-[#0A0A0F]" data-testid="challenge-quiz">
        <div className="max-w-lg mx-auto px-4 py-6 space-y-5">
          {/* Top bar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Swords className="w-5 h-5 text-orange-400" />
              <span className="text-sm font-semibold text-white">Question {currentQ + 1}/{total}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-white/60">
              <Timer className="w-4 h-4" /> {formatTime(timeElapsed)}
            </div>
          </div>

          {/* Progress bar */}
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-orange-500 to-red-500 transition-all" style={{ width: `${((currentQ + 1) / total) * 100}%` }} />
          </div>

          {/* Question */}
          <AnimatePresence mode="wait">
            <motion.div key={currentQ} initial={{ x: 30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -30, opacity: 0 }}>
              <Card className="p-5 rounded-2xl bg-white/5 border-white/10" data-testid={`challenge-q-${currentQ}`}>
                <p className="text-base font-medium text-white mb-4">{q.question}</p>
                <div className="space-y-2">
                  {(q.options || []).map((opt, oi) => {
                    const selected = answers[String(currentQ)] === opt;
                    return (
                      <button
                        key={oi}
                        onClick={() => selectAnswer(opt)}
                        className={`w-full text-left px-4 py-3 rounded-xl text-sm border transition-all ${
                          selected
                            ? 'border-orange-500/50 bg-orange-500/15 text-white'
                            : 'border-white/10 text-white/70 hover:border-white/20 hover:text-white'
                        }`}
                        data-testid={`challenge-q-${currentQ}-opt-${oi}`}
                      >
                        <span className="font-semibold mr-2 text-white/40">{String.fromCharCode(65 + oi)}.</span>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </Card>
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button onClick={prevQuestion} disabled={currentQ === 0} variant="outline" className="rounded-xl border-white/20 text-white/60" data-testid="challenge-prev">
              Back
            </Button>
            <span className="text-xs text-white/40">{answered}/{total} answered</span>
            {currentQ < total - 1 ? (
              <Button onClick={nextQuestion} disabled={!answers[String(currentQ)]} className="rounded-xl gap-1 bg-gradient-to-r from-orange-500 to-red-500" data-testid="challenge-next">
                Next <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button onClick={submitChallenge} disabled={submitting || answered < total} className="rounded-xl gap-1 bg-gradient-to-r from-green-500 to-emerald-500" data-testid="challenge-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Submit
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── RESULTS PHASE ────────────────────────────────
  if (phase === 'results' && results) {
    const beat = results.beat_creator;
    return (
      <div className="min-h-screen bg-[#0A0A0F]" data-testid="challenge-results">
        <div className="max-w-lg mx-auto px-4 py-8 space-y-6">
          {/* Result Banner */}
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center">
            <div className={`w-20 h-20 mx-auto mb-4 rounded-2xl flex items-center justify-center shadow-lg ${beat ? 'bg-gradient-to-br from-yellow-400 to-orange-500 shadow-yellow-500/30' : 'bg-gradient-to-br from-purple-500 to-indigo-500 shadow-purple-500/30'}`}>
              {beat ? <Crown className="w-10 h-10 text-white" /> : <Target className="w-10 h-10 text-white" />}
            </div>
            <h1 className="text-3xl font-bold text-white">{beat ? 'You Won!' : 'Challenge Complete!'}</h1>
            <p className="text-white/60 mt-1">{results.message}</p>
          </motion.div>

          {/* Score Comparison */}
          <Card className="p-5 rounded-2xl bg-white/5 border-white/10" data-testid="challenge-score-comparison">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 rounded-xl bg-orange-500/10 border border-orange-500/20">
                <p className="text-xs text-orange-300 mb-1">{results.creator_name}</p>
                <p className="text-3xl font-bold text-orange-400">{results.creator_score}%</p>
                <p className="text-xs text-white/40">Creator</p>
              </div>
              <div className={`text-center p-4 rounded-xl ${beat ? 'bg-green-500/10 border border-green-500/20' : 'bg-white/5 border border-white/10'}`}>
                <p className="text-xs text-white/60 mb-1">You ({name})</p>
                <p className={`text-3xl font-bold ${beat ? 'text-green-400' : 'text-white'}`}>{results.score}%</p>
                <p className="text-xs text-white/40">Grade {results.grade}</p>
              </div>
            </div>
            <div className="flex justify-center gap-6 mt-4 text-xs text-white/50">
              <span>{results.correct}/{results.total} correct</span>
              <span>{formatTime(results.time_taken_seconds)}</span>
            </div>
          </Card>

          {/* Question Review */}
          <Card className="p-5 rounded-2xl bg-white/5 border-white/10" data-testid="challenge-review">
            <h3 className="text-sm font-semibold text-white mb-3">Review Answers</h3>
            <div className="space-y-3 max-h-60 overflow-y-auto">
              {results.results.map((r, i) => (
                <div key={i} className={`p-3 rounded-lg border text-sm ${r.is_correct ? 'border-green-500/30 bg-green-500/5' : 'border-red-500/30 bg-red-500/5'}`}>
                  <div className="flex items-start gap-2">
                    {r.is_correct ? <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />}
                    <div>
                      <p className="text-white/80">{r.question}</p>
                      {!r.is_correct && <p className="text-xs text-green-300 mt-1">Correct: {r.correct_answer}</p>}
                      {r.explanation && <p className="text-[10px] text-white/40 mt-1">{r.explanation}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Leaderboard */}
          {leaderboard.length > 0 && (
            <Card className="p-4 rounded-2xl bg-white/5 border-white/10" data-testid="results-leaderboard">
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2"><Crown className="w-4 h-4 text-yellow-400" /> Leaderboard</h3>
              <div className="space-y-2">
                {leaderboard.slice(0, 10).map((e, i) => (
                  <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg ${e.name === name ? 'bg-green-500/10 border border-green-500/20' : e.is_creator ? 'bg-orange-500/10' : 'bg-white/[0.03]'}`}>
                    <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${i === 0 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-white/10 text-white/50'}`}>{i + 1}</span>
                    <span className="flex-1 text-sm text-white truncate">{e.name} {e.is_creator && <span className="text-[10px] text-orange-300">(creator)</span>} {e.name === name && !e.is_creator && <span className="text-[10px] text-green-300">(you)</span>}</span>
                    <span className="text-sm font-bold text-white">{e.score}%</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <Button onClick={() => { setPhase('intro'); setAnswers({}); setCurrentQ(0); setResults(null); }} variant="outline" className="flex-1 rounded-xl gap-2 border-white/20 text-white/70" data-testid="challenge-retry">
              <RotateCcw className="w-4 h-4" /> Try Again
            </Button>
            <Button onClick={copyLink} className="flex-1 rounded-xl gap-2 bg-gradient-to-r from-orange-500 to-red-500" data-testid="challenge-share">
              <Copy className="w-4 h-4" /> Copy Link
            </Button>
          </div>
          <div className="text-center">
            <Button onClick={() => shareChallenge(challengeId, challenge?.creator_name, challenge?.creator_score, challenge?.subject_name)}
              className="rounded-xl gap-2 bg-green-600 hover:bg-green-700 text-white" data-testid="challenge-whatsapp">
              <Share2 className="w-4 h-4" /> Share on WhatsApp
            </Button>
          </div>

          <div className="text-center">
            <Link to="/login">
              <Button variant="ghost" className="gap-2 text-white/50 hover:text-white" data-testid="challenge-signup">
                <GraduationCap className="w-4 h-4" /> Join iStudyPro Free <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default ChallengePage;
